// V17.299: pure logic shared by every team-board wall display.
//
// Extracted from ARResidentialTeamBoardTV so a second board (AR Commercial)
// cannot fork it. Duplicated paging or token-claiming would mean a bug gets
// fixed on one wall display and not the other — silently, on a screen nobody
// is watching.
//
// The JSX deliberately stays per-board: it is presentation that diverges by
// segment, and the residential markup carries Chrome-69 wall-TV workarounds
// that must not be disturbed.

import type { TeamBoard, TeamBoardRow } from '../services/arTeamBoard';

export const POLL_INTERVAL_MS = 30_000;
export const PAGE_ROTATE_MS = 15_000;
export const ROWS_PER_PAGE = 12;

/**
 * Pull `?pair=` into storage once. Read through the router's search params
 * (not `window.location` directly) so this works under any router the app
 * mounts with — a real `BrowserRouter` in production and a `MemoryRouter` in
 * tests both keep their own address state, and only the router APIs see both.
 *
 * `storageKey` is per-board: one browser previewing both wall displays must
 * not have one board's token overwrite the other's.
 */
export function claimPairingToken(
  searchParams: URLSearchParams,
  storageKey: string,
): string | null {
  const paired = searchParams.get('pair');
  if (paired) {
    localStorage.setItem(storageKey, paired);
    return paired;
  }
  return localStorage.getItem(storageKey);
}

export function topThree(board: TeamBoard): TeamBoardRow[] {
  return board.teams
    .flatMap((team) => team.rows)
    .sort((a, b) => b.collected_today - a.collected_today)
    .slice(0, 3);
}

/** Split into fixed-size pages, keeping each team's rows with their team. */
export function paginate(
  board: TeamBoard,
  rowsPerPage: number = ROWS_PER_PAGE,
): TeamBoard[] {
  const pages: TeamBoard[] = [];
  let current: TeamBoard['teams'] = [];
  let count = 0;

  for (const team of board.teams) {
    for (let i = 0; i < team.rows.length; i += rowsPerPage) {
      const slice = team.rows.slice(i, i + rowsPerPage);
      if (count + slice.length > rowsPerPage && current.length) {
        pages.push({ ...board, teams: current });
        current = [];
        count = 0;
      }
      current.push({ ...team, rows: slice });
      count += slice.length;
    }
  }
  if (current.length) pages.push({ ...board, teams: current });
  return pages.length ? pages : [board];
}

/**
 * Seconds until the next poll.
 *
 * Rounded, not ceiled: `nextRefreshAt` is stamped a few milliseconds after the
 * 1s clock's own start, so ceiling turns a 30.004s gap into a "31s" that
 * flashes on screen before the first tick. Clamped at 0 because the clock and
 * the poll are separate intervals — the countdown can land a tick past due
 * before the poll restamps it, and a negative number on a wall display would
 * read as a fault.
 */
export function secondsToRefresh(
  nextRefreshAt: number | null,
  now: Date,
): number {
  return Math.max(
    0,
    Math.round(((nextRefreshAt ?? Date.now()) - now.getTime()) / 1000),
  );
}
